import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, WarningCircle, CircleNotch } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function LeadPullConfirmPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const action = params.get("action") === "cancel" ? "cancel" : "confirm";

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Missing confirmation token.");
      setLoading(false);
      return;
    }
    api.get("/lead-pull/confirm", { params: { token } })
      .then(({ data }) => {
        if (!data.valid) setError(data.error || "This confirmation link is no longer valid.");
        else setPreview(data);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load this confirmation."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/lead-pull/confirm", { token, action });
      if (!data.success) setError(data.error || "This request could not be completed.");
      else setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-igc-bg flex items-center justify-center p-6" data-testid="lead-pull-confirm-page">
      <div className="bg-igc-surface border border-igc-gold/20 max-w-sm w-full p-6 text-center">
        <p className="font-serif text-xl text-igc-cream mb-4">IGC Lead Pull</p>

        {loading && (
          <div className="flex flex-col items-center gap-3 text-igc-muted" data-testid="lead-pull-confirm-loading">
            <CircleNotch size={28} className="animate-spin" />
            <p className="text-sm">Loading request...</p>
          </div>
        )}

        {!loading && error && !result && (
          <div className="flex flex-col items-center gap-3" data-testid="lead-pull-confirm-error">
            <WarningCircle size={32} weight="fill" className="text-igc-danger" />
            <p className="text-igc-cream text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && preview && !result && (
          <div className="space-y-4" data-testid="lead-pull-confirm-preview">
            <div className="text-left bg-igc-bg border border-igc-gold/10 p-4 text-sm text-igc-cream space-y-1">
              <p><span className="text-igc-muted">Rep:</span> {preview.rep_name}{preview.rep_code ? ` (${preview.rep_code})` : ""}</p>
              <p><span className="text-igc-muted">Category:</span> {preview.category}</p>
              <p><span className="text-igc-muted">Count:</span> {preview.count}</p>
            </div>
            {action === "cancel" ? (
              <button data-testid="lead-pull-confirm-cancel-button" disabled={submitting} onClick={handleSubmit} className="w-full text-sm px-4 py-2.5 border border-igc-danger/40 text-igc-danger font-medium hover:bg-igc-danger/10 transition-colors disabled:opacity-50">
                {submitting ? "Cancelling..." : "Cancel This Pull"}
              </button>
            ) : (
              <button data-testid="lead-pull-confirm-submit-button" disabled={submitting} onClick={handleSubmit} className="w-full text-sm px-4 py-2.5 bg-igc-gold text-igc-goldText font-medium hover:bg-igc-gold/90 transition-colors disabled:opacity-50">
                {submitting ? "Pulling..." : "Confirm Pull"}
              </button>
            )}
            <p className="text-igc-muted text-xs">This link expires 30 minutes after the request was made and can only be used once.</p>
          </div>
        )}

        {result && (
          <div className="flex flex-col items-center gap-3" data-testid="lead-pull-confirm-success">
            <CheckCircle size={32} weight="fill" className="text-igc-success" />
            <p className="text-igc-cream text-sm whitespace-pre-line">{result.summary || result.message}</p>
          </div>
        )}
      </div>
    </div>
  );
}
